import { motion } from 'framer-motion';
import { Compass, Zap, TrendingUp, Calendar, DollarSign, BarChart3, ArrowRight, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';

const features = [
  {
    icon: Calendar,
    title: 'Event Discovery',
    description: 'AI finds the perfect shows for your craft. Never miss an application deadline again.',
  },
  {
    icon: DollarSign,
    title: 'Smart Pricing',
    description: 'Know exactly what to charge. AI analyzes materials, labor, and market to maximize profit.',
  },
  {
    icon: BarChart3,
    title: 'Multi-Channel Sales',
    description: 'Etsy, boutiques, events, website—track every sale and see true profit after ALL fees.',
  },
  {
    icon: TrendingUp,
    title: 'Business Analytics',
    description: 'Real insights, not spreadsheet headaches. See what sells, where, and why.',
  },
];

const stats = [
  { value: '10K+', label: 'Artisans Trust Us' },
  { value: '40%', label: 'Avg. Profit Increase' },
  { value: '15hrs', label: 'Saved Per Month' },
  { value: '4.9', label: 'Star Rating' },
];

export default function CompassPage() {
  return (
    <div className="page-transition" data-testid="compass-page">
      <SEOHead page="compass" />
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Compass className="w-4 h-4" />
              <span>AMS Compass</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-serif font-semibold text-foreground mb-6">
              Plan & Profit
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed mb-8">
              Your artisan business copilot. Simple tools, zero tech headaches. 
              You're an artist, not a software engineer—let Compass handle the business stuff 
              so you can get back to creating.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="https://www.madeartisanmarkets.com" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button size="lg" className="rounded-full px-8 py-6 text-lg btn-shine" data-testid="compass-cta">
                  Try AMSCompass Free
                  <ExternalLink className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a 
                href="https://www.madeartisanmarkets.com/login" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button variant="outline" size="lg" className="rounded-full px-8 py-6 text-lg" data-testid="compass-signin">
                  Sign In
                </Button>
              </a>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              7-day free trial • No credit card required • Cancel anytime
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <span className="text-4xl md:text-5xl font-serif font-bold">{stat.value}</span>
                <p className="text-white/70 mt-2">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-accent font-accent text-2xl">Stop Wearing All The Hats</span>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-foreground mt-2 mb-6">
              Everything You Need. Nothing You Don't.
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Built specifically for artisan entrepreneurs. No bloated features. 
              Just the tools you'll actually use.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-border rounded-2xl p-8 shadow-sm card-hover"
              >
                <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-amber-600" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-serif font-semibold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-amber-500 to-amber-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <Zap className="w-12 h-12 mx-auto mb-6" strokeWidth={1.5} />
            <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-6">
              Simple Tools. Zero Tech Headaches.
            </h2>
            <p className="text-xl text-white/80 mb-10">
              No coding required. No complex setup. Just sign up and start running your business smarter.
            </p>
            <a 
              href="https://www.madeartisanmarkets.com/signup" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button size="lg" className="bg-white text-amber-600 hover:bg-white/90 rounded-full px-10 py-6 text-lg" data-testid="compass-signup-cta">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
